import { useMemo } from 'react';
import SectionHeading from '@/components/feature/SectionHeading';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';

type Props = {
  query: string;
  setQuery: (value: string) => void;
  category: string;
  setCategory: (value: string) => void;
};

export default function TopicExplorer({ query, setQuery, category, setCategory }: Props) {
  const filtered = useMemo(() => {
    const activeName = categories.find((c) => c.id === category)?.name;
    return articles.filter((item) => {
      const matchCategory = category === 'all' || item.category === activeName;
      const keyword = query.trim().toLowerCase();
      const matchQuery =
        !keyword ||
        item.title.toLowerCase().includes(keyword) ||
        item.summary.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchQuery;
    });
  }, [query, category]);

  return (
    <section id="topics" className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <SectionHeading
          eyebrow="话题分类 · 扫一遍就够"
          title="按门类拆开的今日热议焦点"
          description="影视片场、音乐现场、综艺录制、品牌合作与粉丝圈层，选一个门类先看重点，再决定要不要细读。"
        />

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = category === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  aria-pressed={active}
                  className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                    active
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 bg-background-100 rounded-full pl-4 pr-3 h-11 lg:w-80">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="在话题里搜索关键词"
              aria-label="在话题分类中搜索关键词"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                aria-label="清空搜索关键词"
                onClick={() => setQuery('')}
                className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-circle-fill" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center bg-background-100 rounded-lg border border-dashed border-background-300">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              没有找到匹配「{query}」的话题
            </p>
            <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或者切回「全部话题」看看今天的热点。</p>
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCategory('all');
              }}
              className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}